import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import lodashGet from 'lodash/get';
import { toast } from 'react-toastify';

import { DataTableColumn, DataTableColumnType, EXCLUDED_COLUMN_TYPES } from '../DataTable.types';
import { DATE_FORMATS, formatDateByFormat } from '../../../../utils/dateUtils';
import { getDefaultColumnIds, idsToConfig } from './columnTransformUtils';
import { EXT_MIME } from '../../../DocumentViewer/constants';

export type ExportGrid = {
    headers: string[];
    data: (string | number)[][];
};

// Client-side safety cap on the total rows fetched while paging the on-screen list
// endpoints for export — shared here so every fetchAllXForExport's `truncated` flag
// means the same thing.
export const EXPORT_ROW_CAP = 20000;

// Fixed column width applied to every exported .xlsx column.
const EXPORT_COLUMN_WIDTH = 20;

// Column types with no exportable scalar value. Export-only — the column
// customization drawer still uses the shared EXCLUDED_COLUMN_TYPES, since
// COMPONENT/ADD_COMMENT columns are legitimately customizable on screen.
const EXPORT_EXCLUDED_COLUMN_TYPES = [...EXCLUDED_COLUMN_TYPES, DataTableColumnType.COMPONENT, DataTableColumnType.ADD_COMMENT];

const isPlainObject = (value: unknown): value is Record<string, any> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const getDropdownLabel = (dropdownOptions: DataTableColumn['dropdownOptions'], value: any): string => {
    if (!dropdownOptions || value === null || value === undefined) return '';
    const option = (dropdownOptions as Record<string, any>)[value];
    if (option?.label) return option.label;
    return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
};

/**
 * Generic plain-text coercion for column types with no special handling below.
 *
 * Audit of every DataTableColumnType actually wired
 * into the Deals (DealsRow.tsx) and Leads (CompaniesList.tsx) master configs
 * that falls through to this default branch:
 *  - PILL: no default renderer exists in useCellRenderers.ts and neither page
 *    registers a custom one — this type isn't renderable (or exportable) in
 *    either table today. Left to this generic fallback for forward-compat.
 * (COMPONENT/ADD_COMMENT are dropped up-front via EXPORT_EXCLUDED_COLUMN_TYPES,
 * so they never reach this coercion.)
 * GRADIENT_TEXT, COMMENT and LAST_STATUS_UPDATE (all confirmed object/number
 * shaped) get explicit handling in the switch below instead of falling here.
 */
const toPlainText = (raw: any): string | number => {
    if (raw === null || raw === undefined || raw === '') return '';
    if (typeof raw === 'string' || typeof raw === 'number') return raw;
    if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
    if (Array.isArray(raw)) return raw.filter((item) => typeof item === 'string' || typeof item === 'number').join(', ');
    if (isPlainObject(raw)) {
        if (raw.text ?? raw.label ?? raw.name ?? raw.value) return raw.text ?? raw.label ?? raw.name ?? raw.value;
        // Last resort so an unrecognised object shape still surfaces something
        // useful in the sheet instead of a silent blank cell.
        try {
            return JSON.stringify(raw);
        } catch {
            return '';
        }
    }
    return '';
};

/**
 * Resolves a single cell's value to plain text for the Excel export.
 * Mirrors the on-screen cell renderers (StatusDropdown labels, formatted dates,
 * owner names) so the exported file matches what the user sees on the table,
 * instead of dumping raw objects / "[object Object]" into the sheet.
 *
 * Dispatches on `column.type` FIRST (not on the raw value's JS type) — a date
 * is a `number` and a dropdown code is a `string`, so a naive "return
 * primitives as-is" shortcut would skip the formatting/label lookup entirely.
 */
export const getExportCellValue = (
    row: any,
    column: DataTableColumn,
    valueGetterMap?: Record<string, (row: any) => any>,
): string | number => {
    const raw = valueGetterMap?.[column.field] ? valueGetterMap[column.field](row) : lodashGet(row, column.field, '');

    switch (column.type) {
        case DataTableColumnType.DATE_FORMAT:
        case DataTableColumnType.DATE_PICKER: {
            const dateValue = isPlainObject(raw) ? lodashGet(row, column.field) : raw;
            if (dateValue === null || dateValue === undefined || dateValue === '') return '';
            return formatDateByFormat(dateValue, column.dateFormat || DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY);
        }
        case DataTableColumnType.SINGLE_SELECT_DROPDOWN: {
            const value = lodashGet(row, column.field);
            if (value === null || value === undefined || value === '') return '';
            return getDropdownLabel(column.dropdownOptions, value);
        }
        case DataTableColumnType.MULTI_SELECT_DROPDOWN: {
            const values = lodashGet(row, column.field);
            if (Array.isArray(values)) {
                return values.map((value) => getDropdownLabel(column.dropdownOptions, value)).join(', ');
            }
            return typeof values === 'string' ? values : '';
        }
        case DataTableColumnType.OWNER: {
            if (Array.isArray(raw)) {
                return raw.map((owner) => owner?.name || owner?.label || '').join(', ');
            }
            if (isPlainObject(raw)) return raw?.name || raw?.label || '';
            // Owner columns on screen (OwnerCellRenderer.tsx) store a bare userId
            // and resolve it to a display name against
            // `state.investor.ownerDropdownOptions`. This function has no Redux
            // access, so callers (useDataTableExport.ts) MUST pass a
            // `valueGetterMap` entry for owner fields that does that userId -> name
            // lookup — `raw` here is already whatever that getter returned. If no
            // getter was supplied, this intentionally passes the raw userId
            // through rather than silently blanking it, so a missing resolver is
            // visible in the exported file instead of hidden.
            return typeof raw === 'string' ? raw : '';
        }
        case DataTableColumnType.GRADIENT_TEXT: {
            // Mirrors GradientTextCellRenderer.tsx: value is either a plain string
            // or a { text, subText? } object.
            if (typeof raw === 'string') return raw;
            if (isPlainObject(raw)) return [raw.text, raw.subText].filter(Boolean).join(' — ');
            return '';
        }
        case DataTableColumnType.COMMENT: {
            // Mirrors CommentCellRenderer.tsx: value is { text, activityBy, activityDate, userId }.
            if (isPlainObject(raw)) return raw.text || '';
            return typeof raw === 'string' ? raw : '';
        }
        case DataTableColumnType.LAST_STATUS_UPDATE: {
            // LastStatusUpdateDisplay (types/statusTimeline.ts) is a raw epoch-ms
            // number — format it instead of exporting the raw timestamp.
            if (raw === null || raw === undefined || raw === '') return '';
            return formatDateByFormat(raw, DATE_FORMATS.TIME_DATE_FORMAT_HH_MM_A_DD_MMM_YYYY);
        }
        default:
            return toPlainText(raw);
    }
};

/**
 * Resolves the ordered, visible columns for export (respecting column
 * customization show/hide + order) and converts every row into a plain-text
 * grid. This reproduces exactly what `DataTable.tsx` renders on screen.
 */
export const buildExportGrid = (
    masterConfig: DataTableColumn[],
    selectedColumns: string[],
    rows: any[],
    valueGetterMap?: Record<string, (row: any) => any>,
): ExportGrid => {
    const idsToUse = selectedColumns?.length ? selectedColumns : getDefaultColumnIds(masterConfig ?? []);
    const orderedColumns = idsToConfig(idsToUse, masterConfig ?? []);
    const exportableColumns = orderedColumns.filter(
        (column) =>
            (column.show === undefined || column.show === true) &&
            // Excluded types (COMPONENT/ADD_COMMENT) have no scalar of their own, so they're
            // dropped — UNLESS the caller supplies an explicit export value-getter for that
            // field (e.g. the Deals "Probability" component column, exported from its raw
            // customFields.debtOptionProbability value).
            (!EXPORT_EXCLUDED_COLUMN_TYPES.includes(column.type) || !!valueGetterMap?.[column.field]),
    );

    const headers = exportableColumns.map((column) =>
        typeof column.headerLabel === 'string' ? column.headerLabel : column.headerOriginalLabel,
    );
    const data = (rows ?? []).map((row) => exportableColumns.map((column) => getExportCellValue(row, column, valueGetterMap)));

    return { headers, data };
};

export type ExportRowsToExcelParams = {
    sheetName: string;
    headers: string[];
    data: (string | number)[][];
    fileName: string;
};

// Shared failure message for client-side Excel generation (used by every export path).
export const EXCEL_GENERATION_FAILED_MESSAGE = 'Failed to generate Excel file';

/** Serialise an ExcelJS workbook to an .xlsx Blob — the one place the workbook→blob step lives. */
export const workbookToXlsxBlob = async (workbook: ExcelJS.Workbook): Promise<Blob> => {
    const buffer = await workbook.xlsx.writeBuffer();
    return new Blob([buffer], { type: EXT_MIME.xlsx });
};

/**
 * Generates and downloads an .xlsx file from a plain-text grid.
 * Returns false (and shows a toast) if workbook generation fails, so the
 * caller can decide whether to still fire success side-effects (e.g. Mixpanel).
 */
export const exportRowsToExcel = async ({ sheetName, headers, data, fileName }: ExportRowsToExcelParams): Promise<boolean> => {
    try {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(sheetName);

        worksheet.addRow(headers);
        data.forEach((row) => worksheet.addRow(row));

        worksheet.columns.forEach((column) => {
            column.width = EXPORT_COLUMN_WIDTH;
        });

        saveAs(await workbookToXlsxBlob(workbook), fileName);
        return true;
    } catch (error) {
        console.error('Export to Excel failed', error);
        toast.error(EXCEL_GENERATION_FAILED_MESSAGE);
        return false;
    }
};
